import React, { useMemo, useState } from 'react';
import { useListSiteFeedbackQuery } from '../app/api';
import { useIsMobile } from './utils';
import PropTypes from 'prop-types';


const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#b00020',
  amber:     '#b06f00',
};

const CATEGORY_STYLE = {
  bug:   { bg: 'rgba(176,0,32,0.10)',   color: brand.red,       label: 'Bug',   icon: 'bug-fill' },
  idea:  { bg: 'rgba(208,155,44,0.18)', color: brand.goldenrod, label: 'Idea',  icon: 'lightbulb-fill' },
  other: { bg: 'rgba(88,88,91,0.15)',   color: brand.charcoal,  label: 'Other', icon: 'chat-square-dots' },
};

function CategoryPill({ category }) {
  const s = CATEGORY_STYLE[category] || CATEGORY_STYLE.other;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
      background: s.bg, color: s.color,
      fontWeight: 700, fontSize: '0.7rem',
      textTransform: 'uppercase', letterSpacing: '0.5px',
      padding: '0.2rem 0.55rem', borderRadius: 999,
    }}>
      <i className={`bi bi-${s.icon}`} />
      {s.label}
    </span>
  );
}

CategoryPill.propTypes = {
  category: PropTypes.string,
};


function formatTimestamp(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export default function Feedback() {
  const isMobile = useIsMobile();
  const { data: rows = [], isLoading, isError } = useListSiteFeedbackQuery();
  const [filter, setFilter] = useState('all'); // 'all' | category key

  const filtered = useMemo(() => {
    if (filter === 'all') return rows;
    return rows.filter(r => r.category === filter);
  }, [rows, filter]);

  const counts = useMemo(() => {
    const acc = { bug: 0, idea: 0, other: 0 };
    rows.forEach(r => {
      if (acc[r.category] !== undefined) acc[r.category] += 1;
    });
    return acc;
  }, [rows]);

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: isMobile ? '0.75rem' : '1.5rem' }}>

      {/* Header banner */}
      <div style={{ background: brand.deepBlue, borderRadius: '12px', padding: isMobile ? '1.25rem' : '1.75rem 2rem', marginBottom: isMobile ? '1rem' : '1.5rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: brand.goldenrod }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ background: 'rgba(208,155,44,0.15)', border: `2px solid ${brand.goldenrod}`, borderRadius: '12px', padding: '0.75rem', flexShrink: 0 }}>
            <i className="bi bi-megaphone-fill" style={{ color: brand.goldenrod, fontSize: '1.75rem' }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <p style={{ color: brand.goldenrod, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 4px' }}>
              Configuration
            </p>
            <h3 style={{ color: 'white', fontWeight: 800, margin: '0 0 4px', fontSize: isMobile ? '1.25rem' : undefined }}>Hub Feedback</h3>
            <p style={{ color: 'rgba(255,255,255,0.65)', margin: 0, fontSize: '0.875rem' }}>
              Everything employees have submitted via the Feedback button, newest first.
            </p>
          </div>
        </div>
      </div>

      {/* Filter tabs */}
      <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 8px rgba(10,41,71,0.06)', padding: '0.6rem 0.85rem', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        {[
          { value: 'all',   label: 'All',   count: rows.length },
          { value: 'bug',   label: 'Bugs',  count: counts.bug },
          { value: 'idea',  label: 'Ideas', count: counts.idea },
          { value: 'other', label: 'Other', count: counts.other },
        ].map(t => {
          const active = filter === t.value;
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => setFilter(t.value)}
              style={{
                background: active ? brand.deepBlue : 'white',
                color: active ? 'white' : brand.deepBlue,
                border: `1px solid ${active ? brand.deepBlue : brand.softGray}`,
                borderRadius: 999,
                padding: '0.3rem 0.85rem',
                fontWeight: 700, fontSize: '0.8rem',
                cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
              }}
            >
              {t.label}
              <span style={{
                background: active ? 'rgba(255,255,255,0.18)' : brand.softGray,
                color: active ? 'white' : brand.charcoal,
                borderRadius: 999, padding: '0 0.45rem', fontSize: '0.72rem',
              }}>{t.count}</span>
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <div style={{ padding: '3rem', textAlign: 'center' }}>
          <div className="spinner-border" style={{ color: brand.tealBlue }} />
        </div>
      ) : isError ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: brand.charcoal, background: 'white', borderRadius: 12 }}>
          <i className="bi bi-exclamation-triangle" style={{ fontSize: '1.5rem', color: brand.goldenrod, display: 'block', marginBottom: '0.5rem' }} />
          Unable to load feedback. Try refreshing the page.
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: brand.charcoal, background: 'white', borderRadius: 12 }}>
          <i className="bi bi-inbox" style={{ fontSize: '2.5rem', opacity: 0.4, display: 'block', marginBottom: '0.5rem' }} />
          {filter === 'all'
            ? 'No feedback yet. When employees send something via the Feedback button, it will show up here.'
            : `No feedback in this category yet.`}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filtered.map(row => {
            const submitter = (row.first_name || row.last_name)
              ? `${row.first_name || ''} ${row.last_name || ''}`.trim()
              : (row.final_email || row.username || 'Unknown');
            return (
              <div key={row.id} style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 12px rgba(10,41,71,0.08)', padding: isMobile ? '0.85rem 1rem' : '1rem 1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                  <CategoryPill category={row.category} />
                  <span style={{ color: brand.deepBlue, fontWeight: 700, fontSize: '0.9rem' }}>{submitter}</span>
                  {row.final_email && (
                    <span style={{ color: brand.charcoal, fontSize: '0.8rem' }}>{row.final_email}</span>
                  )}
                  <span style={{ marginLeft: 'auto', color: brand.charcoal, fontSize: '0.78rem' }}>
                    {formatTimestamp(row.created)}
                  </span>
                </div>
                <div style={{ color: brand.deepBlue, fontSize: '0.95rem', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {row.message}
                </div>
                <div style={{ marginTop: '0.6rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1.25rem', fontSize: '0.75rem', color: brand.charcoal }}>
                  {row.page_url && (
                    <span><i className="bi bi-link-45deg me-1" /><code style={{ color: brand.charcoal }}>{row.page_url}</code></span>
                  )}
                  {row.user_agent && (
                    <span title={row.user_agent} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 360 }}>
                      <i className="bi bi-browser-chrome me-1" />{row.user_agent}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
